import { useEffect, useState } from 'react';
import { formatDate } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  dismissAccessRequest,
  grantAccess,
  listAccessRequests,
  type AccessRequestUser,
} from '../../api/admin';
import { listTenants, type Tenant } from '../../api/tenants';
import { extractErrorMessage } from '../../api/errors';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useTableSort, sortRows } from '../../util/tableSort';

type GrantRole = 'superadmin' | 'tenant_admin' | 'participant';

const ROLE_OPTIONS: { value: GrantRole; label: string }[] = [
  { value: 'participant', label: 'Participant' },
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'superadmin', label: 'Superadmin' },
];

type RequestSortKey = 'email' | 'full_name' | 'created_at' | 'registered_ip';

/**
 * Superadmin "Access requests" — self-registered users with no active
 * membership. Grant a role (optionally tenant-scoped) directly, the
 * in-app alternative to email invitations.
 */
export function AccessRequests({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [requests, setRequests] = useState<AccessRequestUser[] | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [target, setTarget] = useState<AccessRequestUser | null>(null);
  const [role, setRole] = useState<GrantRole>('participant');
  const [tenantId, setTenantId] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Dismiss (delete a roleless signup) confirm state.
  const [dismissTarget, setDismissTarget] = useState<AccessRequestUser | null>(
    null,
  );
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState(false);
  // Click-to-sort headers, matching the other admin list tables.
  const sort = useTableSort<RequestSortKey>('email');

  async function load() {
    setError(null);
    try {
      const [reqs, ts] = await Promise.all([listAccessRequests(), listTenants()]);
      setRequests(reqs);
      setTenants(ts.filter((t) => t.is_active));
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to load access requests'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const openGrant = (u: AccessRequestUser) => {
    setTarget(u);
    setRole('participant');
    setTenantId('');
    setSubmitError(null);
  };

  const submitGrant = async () => {
    if (!target) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await grantAccess(
        target.id,
        role,
        role === 'superadmin' ? null : tenantId || null,
      );
      setTarget(null);
      await load();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'could not grant access'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDismiss = async () => {
    if (!dismissTarget) return;
    setDismissError(null);
    setDismissing(true);
    try {
      await dismissAccessRequest(dismissTarget.id);
      setDismissTarget(null);
      await load();
    } catch (err) {
      setDismissError(extractErrorMessage(err, 'could not dismiss this request'));
    } finally {
      setDismissing(false);
    }
  };

  const grantDisabled =
    submitting || (role !== 'superadmin' && !tenantId);

  return (
    <Stack spacing={3}>
      {!embedded && (
        <>
          <Typography variant="h4">Access requests</Typography>
          <Typography color="text.secondary">
            Signed-up users who don't have a role yet. Grant one to let them
            in.
          </Typography>
        </>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Card>
        {requests === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No pending users.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortHeader label="Email" col="email" sort={sort} />
                  <SortHeader label="Name" col="full_name" sort={sort} />
                  <SortHeader label="Registered" col="created_at" sort={sort} />
                  {/* Signup origin (AUTH-IP-LOGGING). Nobody vouched for a
                      self-registration, so the origin is a signal when
                      deciding grant vs dismiss — and sorting by it groups a
                      burst of signups from one address together. */}
                  <SortHeader label="IP address" col="registered_ip" sort={sort} />
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortRows(
                  requests,
                  (u) => {
                    switch (sort.orderBy) {
                      case 'email':
                        return u.email;
                      case 'full_name':
                        return u.full_name;
                      case 'created_at':
                        return u.created_at;
                      case 'registered_ip':
                        // Empty reads as "unknown" to the shared comparator,
                        // which parks those rows last in both directions —
                        // same convention as every other table here.
                        return u.registered_ip ?? '';
                    }
                  },
                  sort.order,
                ).map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.full_name ?? '—'}</TableCell>
                    <TableCell>
                      {formatDate(u.created_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.registered_ip ?? (
                        <Tooltip title="This account registered before the sign-up IP was recorded">
                          <Box component="span" sx={{ fontFamily: 'inherit' }}>
                            —
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openGrant(u)}
                        >
                          Grant access
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => {
                            setDismissError(null);
                            setDismissTarget(u);
                          }}
                        >
                          Dismiss
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={target !== null} onClose={() => setTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Grant access</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {target?.email}
            </Typography>
            <SearchableSelect<GrantRole>
              id="grant-role"
              label="Role"
              size="medium"
              fullWidth
              value={role}
              onChange={setRole}
              options={ROLE_OPTIONS}
            />
            {role !== 'superadmin' && (
              <SearchableSelect
                id="grant-tenant"
                label="Tenant"
                size="medium"
                fullWidth
                value={tenantId}
                onChange={setTenantId}
                options={tenants.map((t) => ({ value: t.id, label: t.name }))}
              />
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={grantDisabled} onClick={submitGrant}>
            {submitting ? 'Granting…' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dismissTarget !== null}
        onClose={() => (dismissing ? null : setDismissTarget(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Dismiss access request?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This permanently deletes the self-registered account{' '}
              <strong>{dismissTarget?.email}</strong>. They hold no role, so
              nothing else is affected. If they still need access later, they
              can register again. This can't be undone.
            </Typography>
            {dismissError && <Alert severity="error">{dismissError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDismissTarget(null)}
            disabled={dismissing}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={dismissing}
            onClick={() => void confirmDismiss()}
          >
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
